import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail, BRAND } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Loader2, Mail, CheckCircle2, KeyRound } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { dev_link? }
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!/\S+@\S+\.\S+/.test(email)) { setError("Enter a valid email."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/password/forgot", { email: email.trim().toLowerCase() });
      setDone({ dev_link: data?.dev_link || null });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not send reset link.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-secondary px-6 py-10">
      <Card className="w-full max-w-md p-8 shadow-xl">
        <div className="mb-6 flex items-center gap-3">
          <img src={BRAND.icon} alt="" className="h-10 w-10" />
          <div className="leading-tight">
            <div className="font-heading text-lg font-semibold text-brand">{BRAND.name}</div>
            <div className="text-[10px] text-ink-muted">{BRAND.tagline}</div>
          </div>
        </div>

        {!done ? (
          <>
            <div className="mb-1 grid h-10 w-10 place-items-center rounded-full bg-brand/10 text-brand"><KeyRound size={18} /></div>
            <h2 className="mt-3 font-heading text-2xl font-semibold">Forgot your password?</h2>
            <p className="mt-1 text-sm text-ink-muted">Enter your account email and we'll send you a link to reset it.</p>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-form">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Email</Label>
                <Input
                  data-testid="forgot-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@hospital.in"
                  className="mt-1.5"
                  required
                />
              </div>
              {error && <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid="forgot-error">{error}</div>}
              <Button data-testid="forgot-submit" type="submit" disabled={submitting} className="w-full bg-brand hover:bg-brand-700">
                {submitting ? <><Loader2 size={14} className="mr-2 animate-spin" /> Sending…</> : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-success" className="space-y-4">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-leaf/15 text-leaf-700"><CheckCircle2 size={24} /></div>
            <h2 className="font-heading text-2xl font-semibold">Check your inbox</h2>
            <p className="text-sm text-ink-muted">
              If an account with <b className="text-ink">{email}</b> exists, we've sent a reset link.
              The link is valid for <b>1 hour</b>.
            </p>
            {done.dev_link && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                <b>SMTP not configured (dev mode):</b><br />
                <a href={done.dev_link} className="font-mono break-all text-amber-900 underline">{done.dev_link}</a>
              </div>
            )}
            <p className="rounded-md bg-secondary p-3 text-[11px] text-ink-muted">
              <Mail size={11} className="mr-1 inline" /> Didn't get it? Check spam, or wait a minute and{" "}
              <button type="button" onClick={() => { setDone(null); }} className="font-medium text-brand hover:underline">try again</button>.
            </p>
          </div>
        )}

        <div className="mt-8 text-center text-[11px] text-ink-muted">
          <Link to="/login" className="inline-flex items-center gap-1 hover:text-brand" data-testid="forgot-back-login">
            <ArrowLeft size={11} /> Back to sign in
          </Link>
        </div>
      </Card>
    </div>
  );
}
